/**
 * DataEntryTable — the whole file at a glance (ADR-0037, Turbo's DataEntryTable): state, kind and
 * link per row, bulk approve of the rows the agent is sure about; clicking a row opens it in the card.
 */
import { useMemo, useState } from "react";
import { Button, Checkbox, Select, Tag } from "@NP-Webber/crm-kit/dist/next/index.js";
import type { RowKind } from "../../../api/aiDataEntryApi";
import { isFinished, KIND_META, KIND_ORDER, money, rowProblems, rowState, shortDate, STATE_META, type LocalRow } from "./dataEntryModel";

const PAGE = 150;

export default function DataEntryTable({
  rows,
  visible,
  kinds,
  hasLedger,
  onOpen,
  onKind,
  onApproveMany,
}: {
  rows: LocalRow[];
  visible: number[];
  kinds: RowKind[];
  hasLedger: boolean;
  onOpen: (i: number) => void;
  onKind: (i: number, k: RowKind) => void;
  onApproveMany: (indexes: number[]) => void;
}) {
  const [sel, setSel] = useState<Set<number>>(() => new Set());
  const [page, setPage] = useState(0);
  const list = useMemo(() => visible.map((i) => rows[i]).filter((r): r is LocalRow => Boolean(r)), [visible, rows]);
  const shown = list.slice(page * PAGE, (page + 1) * PAGE);
  const pages = Math.max(1, Math.ceil(list.length / PAGE));
  const selectable = shown.filter((r) => !isFinished(r));
  const allOn = selectable.length > 0 && selectable.every((r) => sel.has(r.index));
  const toggle = (i: number, on: boolean) =>
    setSel((s) => {
      const n = new Set(s);
      if (on) n.add(i);
      else n.delete(i);
      return n;
    });
  const selectedRows = Array.from(sel)
    .map((i) => rows[i])
    .filter((r): r is LocalRow => Boolean(r) && !isFinished(r));
  const approvable = selectedRows.filter((r) => !rowProblems(r, { hasLedger }).length);
  const kindOptions = KIND_ORDER.filter((k) => kinds.includes(k)).map((k) => ({ value: k, label: KIND_META[k].label }));

  return (
    <div className="de-table">
      {sel.size ? (
        <div className="de-table__bulk">
          <b>{selectedRows.length} נבחרו</b>
          <Button
            size="sm"
            variant="primary"
            icon="check"
            disabled={!approvable.length}
            onClick={() => {
              onApproveMany(approvable.map((r) => r.index));
              setSel(new Set());
            }}
          >
            אישור {approvable.length}
            {approvable.length < selectedRows.length ? ` (${selectedRows.length - approvable.length} דורשות השלמה)` : ""}
          </Button>
          <Select value="" placeholder="שינוי סוג ל…" options={kindOptions} onChange={(k) => k && selectedRows.forEach((r) => onKind(r.index, k as RowKind))} aria-label="שינוי סוג לנבחרות" />
          <Button size="sm" onClick={() => setSel(new Set())}>
            ניקוי
          </Button>
        </div>
      ) : null}
      <div className="de-table__scroll">
        <table>
          <thead>
            <tr>
              <th>
                <Checkbox checked={allOn} onChange={(on) => setSel(on ? new Set(selectable.map((r) => r.index)) : new Set())} aria-label="בחירת הכל" />
              </th>
              <th>שורה</th>
              <th>מצב</th>
              <th>תאריך</th>
              <th>סכום</th>
              <th>סוג</th>
              <th>שיוך</th>
              <th>קטגוריה</th>
              <th>תיאור</th>
              <th>הערות</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const st = rowState(r);
              const dir = KIND_META[r.kind].dir;
              const probs = rowProblems(r, { hasLedger });
              return (
                <tr
                  key={r.index}
                  className={`is-${st}`}
                  onClick={(e) => {
                    if (!(e.target as HTMLElement).closest("input,select,button,label,.ck-select")) onOpen(r.index);
                  }}
                >
                  <td>{isFinished(r) ? null : <Checkbox checked={sel.has(r.index)} onChange={(on) => toggle(r.index, on)} aria-label={`בחירת שורה ${r.line}`} />}</td>
                  <td className="de-muted">{r.line}</td>
                  <td>
                    <Tag tone={STATE_META[st].tone}>{STATE_META[st].label}</Tag>
                  </td>
                  <td>{shortDate(r.date)}</td>
                  <td className={`de-num de-num--${dir ?? "none"}`}>
                    {dir === "out" ? "−" : dir === "in" ? "+" : ""}
                    {money(r.amount, r.currency)}
                  </td>
                  <td>
                    {isFinished(r) || r.existing ? (
                      KIND_META[r.kind].label
                    ) : (
                      <Select value={r.kind} options={kindOptions} onChange={(k) => onKind(r.index, k as RowKind)} aria-label="סוג" />
                    )}
                  </td>
                  <td className="de-ellip">{r.counterparty?.label || (r.newEntity?.name ? <span className="de-muted">חדש: {r.newEntity.name}</span> : "—")}</td>
                  <td className="de-ellip">{r.category?.name || "—"}</td>
                  <td className="de-ellip" title={r.source}>
                    {r.description || r.source}
                  </td>
                  <td className="de-flags">
                    {r.existing ? (
                      <Tag tone="violet" icon="link">
                        {r.existingAction === "create" ? "קיימת · לרשום בכל זאת" : "קיימת · ללא שינוי"}
                      </Tag>
                    ) : null}
                    {r.duplicate ? (
                      <Tag tone="warning" icon="copy">
                        כפילות
                      </Tag>
                    ) : null}
                    {r.kind === "payment" && r.useDeal && r.deals.length ? (
                      <Tag tone="info" icon="link">
                        עסקה
                      </Tag>
                    ) : null}
                    {r.recurrence ? (
                      <Tag tone="info" icon="repeat">
                        מחזורי
                      </Tag>
                    ) : null}
                    {!isFinished(r) && probs.length ? (
                      <Tag tone="warning" icon="alert">
                        {probs[0]!.text}
                      </Tag>
                    ) : null}
                    {r.commit.state === "error" ? <Tag tone="danger">{r.commit.error}</Tag> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pages > 1 ? (
        <div className="de-table__pages">
          <Button size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            הקודם
          </Button>
          <span className="de-muted">
            עמוד {page + 1} מתוך {pages}
          </span>
          <Button size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>
            הבא
          </Button>
        </div>
      ) : null}
    </div>
  );
}
